package org.teddapp.project.ui.settings

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.ui.AppSettings
import org.teddapp.project.ui.Language
import org.teddapp.project.ui.LocalAppSettings
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.ThemeMode
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.ui.semantics.Role
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddConfirmDialog
import org.teddapp.project.ui.TeddSize
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddCardShadow
import org.teddapp.project.ui.teddClickable

/**
 * Réglages : profil, apparence, langue, notifications, modules, données.
 *
 * Utilisable comme drawer 480dp (desktop) ou plein écran (phone) via `modifier`.
 * Les changements s'appliquent immédiatement via `LocalAppSettings.current`.
 */
@Composable
fun SettingsScreen(
    onClose: () -> Unit,
    modifier: Modifier = Modifier.width(480.dp).fillMaxHeight(),
    settings: AppSettings = LocalAppSettings.current,
    /** Callback "Réinitialiser toutes les données". Bouton masqué si null. */
    onResetAll: (() -> Unit)? = null,
    /** Callback "Ouvrir Backup & Sync". Bouton masqué si null. */
    onOpenBackup: (() -> Unit)? = null,
    /** Ouvrir Portfolio & recueil de compétences (absent de la barre du bas sur téléphone). */
    onOpenPortfolio: (() -> Unit)? = null,
    /** État sync exposé par l'app. Si null, section SYNC masquée. */
    syncState: org.teddapp.project.sync.SyncUiState? = null,
    /** Activer / désactiver la sync cloud. */
    onToggleSync: ((Boolean) -> Unit)? = null,
    /** Déclencher un cycle sync à la demande. */
    onSyncNow: (() -> Unit)? = null,
) {
    Column(
        modifier = modifier
            .background(TeddTokens.Bg)
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = {},
            ),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(start = TeddSpace.S6, end = TeddSpace.S4, top = TeddSpace.S4, bottom = TeddSpace.S4),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text("Réglages", style = TeddType.Title2, color = TeddTokens.Ink, modifier = Modifier.weight(1f))
            TeddIconButton(onClick = onClose) {
                TeddIcon(name = "x", size = 16.dp, color = TeddTokens.InkMuted)
            }
        }
        Box(Modifier.fillMaxWidth().height(1.dp).background(TeddTokens.Line))
        // ── Body ──
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(horizontal = TeddSpace.S5, vertical = TeddSpace.S5),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S6),
        ) {
                        SettingsSection(label = "Profil", subtitle = "Ton nom sert à la salutation et à tes initiales.") {
                Column(modifier = Modifier.padding(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                    TeddField(
                        label = "Nom complet",
                        value = settings.fullname,
                        onValueChange = { settings.applyFullname(it) },
                        placeholder = "Ex : Léa Dupont",
                        leadingIcon = { TeddIcon(name = "users", size = 14.dp, color = TeddTokens.InkMuted) },
                    )
                }
            }

            org.teddapp.project.server.LocalServerSession.current?.let { session ->
                SettingsSection(label = "Serveur", subtitle = "Le compte de cet appareil sur le serveur teddApp. S'y connecter y envoie tes données et remplace Supabase.") {
                    ServerSection(session)
                }
                SettingsSection(label = "Assistant", subtitle = "Le modèle de langue qui répond à tes questions sur une compétence : un service en ligne avec ta clé, ou ton propre serveur.") {
                    AssistantModelSection(session)
                }
            }
            SettingsSection(label = "Apparence", subtitle = "Clair le jour, sombre le soir, ou comme ton appareil.") {
                SettingsChoiceRow(
                    label = "Thème",
                    options = listOf(ThemeMode.Light.name to "Clair", ThemeMode.Dark.name to "Sombre", ThemeMode.System.name to "Système"),
                    selected = settings.themeMode.name,
                    onChange = { settings.applyThemeMode(ThemeMode.valueOf(it)) },
                )
            }
            // ── Synchronisation ──
            if (syncState != null && onToggleSync != null) {
                SettingsSection(
                    label = "Synchronisation",
                    subtitle = "Envoie tes données au serveur et reçoit celles de tes autres appareils.",
                ) {
                    SyncStatusCard(
                        state = syncState,
                        onToggle = onToggleSync,
                        onSyncNow = onSyncNow,
                    )
                }
            }

            // ── Finance ──
            SettingsSection(label = "Finance", subtitle = "Ce que le module Finance affiche à l'ouverture.") {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    SettingsToggle(
                        icon = "eye-off",
                        title = "Masquer les soldes par défaut",
                        description = "Les montants sont remplacés par ••••• jusqu'à ce que tu touches l'icône œil.",
                        on = settings.hideBalance,
                        onChange = { settings.applyHideBalance(it) },
                    )
                }
            }

            // ── Données ──
            SettingsSection(label = "Données", subtitle = "Sauvegarde, export, et réinitialisation.") {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    if (onOpenPortfolio != null) {
                        SettingsActionRow(
                            icon = "cards",
                            title = "Portfolio et recueil de compétences",
                            description = "Référentiel de compétences auto-évalué, pièces de portfolio, export.",
                            destructive = false,
                            onClick = onOpenPortfolio,
                        )
                    }
                    if (onOpenBackup != null) {
                        SettingsActionRow(
                            icon = "spark",
                            title = "Sauvegarde et synchro",
                            description = "Historique, exports JSON/SQL, connexion Supabase.",
                            destructive = false,
                            onClick = onOpenBackup,
                        )
                    }
                    if (onResetAll != null) {
                        var confirmReset by remember { mutableStateOf(false) }
                        SettingsActionRow(
                            icon = "trash",
                            title = "Réinitialiser toutes les données",
                            description = "Supprime objectifs, projets, finances, notes, habitudes. Action irréversible.",
                            destructive = true,
                            onClick = { confirmReset = true },
                        )
                        if (confirmReset) {
                            ResetConfirmDialog(
                                onCancel = { confirmReset = false },
                                onConfirm = {
                                    confirmReset = false
                                    onResetAll()
                                },
                            )
                        }
                    }
                    if (onOpenBackup == null && onResetAll == null) {
                        Text(
                            "Les options de sauvegarde et de réinitialisation s'affichent ici lorsqu'elles sont disponibles.",
                            color = TeddTokens.InkMuted,
                            fontSize = 12.sp,
                        )
                    }
                }
            }

            // ── À propos ──
            SettingsSection(
                label = "Noyau",
                subtitle = "Le moteur Rust : journal des faits, projections, réactions, agent.",
            ) {
                Box(modifier = Modifier.padding(TeddSpace.S2)) { org.teddapp.project.core.CoreDiagnosticsPanel() }
            }

            SettingsSection(
                label = "À propos",
                subtitle = "Apprendre, mener, écrire, compter : un noyau, hors ligne d'abord.",
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    // Le numéro vient de gradle.properties, comme celui des paquets :
                    // l'écran ne peut plus annoncer une version que personne n'a livrée.
                    AboutRow(label = "Version", value = org.teddapp.project.TeddVersion.NAME)
                    AboutRow(label = "Noyau", value = org.teddapp.project.core.LocalCoreSession.current?.bridge?.coreVersion() ?: "absent")
                }
            }

            Spacer(modifier = Modifier.height(8.dp))
        }
    }
}

@Composable
internal fun SettingsSection(
    label: String,
    subtitle: String,
    content: @Composable () -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
        Column(modifier = Modifier.padding(horizontal = TeddSpace.S1), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(label, color = TeddTokens.Ink, style = TeddType.BodyStrong)
            Text(subtitle, color = TeddTokens.InkMuted, style = TeddType.Caption)
        }
        // Une carte par section. Les lignes qu'elle contient portent leur propre marge,
        // pour que le survol d'une ligne aille presque jusqu'au bord de la carte.
        TeddCard(
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(TeddTokens.RMd),
            padding = PaddingValues(TeddSpace.S2),
        ) { content() }
    }
}

/** Interrupteur : allumé, piste citron vert et bouton d'encre. */
@Composable
private fun SettingsSwitch(on: Boolean) {
    Box(
        modifier = Modifier
            .size(width = 36.dp, height = 20.dp)
            .background(if (on) TeddTokens.Lime else TeddTokens.LineStrong, RoundedCornerShape(TeddTokens.RFull)),
        contentAlignment = if (on) Alignment.CenterEnd else Alignment.CenterStart,
    ) {
        Box(
            modifier = Modifier
                .padding(horizontal = 2.dp)
                .size(16.dp)
                .background(if (on) TeddTokens.OnLime else TeddTokens.Surface, CircleShape),
        )
    }
}

@Composable
private fun SettingsToggle(
    icon: String,
    title: String,
    description: String,
    on: Boolean,
    onChange: (Boolean) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .teddClickable(onClick = { onChange(!on) }, role = Role.Switch)
            .padding(TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        TeddIcon(name = icon, size = 18.dp, color = TeddTokens.InkMuted)
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(title, color = TeddTokens.Ink, style = TeddType.Label)
            Text(description, color = TeddTokens.InkMuted, style = TeddType.Caption)
        }
        SettingsSwitch(on)
    }
}

/** Choix exclusif entre quelques valeurs : une piste en retrait, la valeur choisie posée dessus. */
@Composable
private fun SettingsChoiceRow(
    label: String,
    options: List<Pair<String, String>>, // valeur → libellé
    selected: String,
    onChange: (String) -> Unit,
) {
    Column(modifier = Modifier.padding(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
        Text(label, color = TeddTokens.Ink, style = TeddType.Label)
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm + 3.dp))
                .padding(3.dp),
            horizontalArrangement = Arrangement.spacedBy(2.dp),
        ) {
            options.forEach { (value, lbl) ->
                val on = value == selected
                val shape = RoundedCornerShape(TeddTokens.RSm)
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .then(if (on) Modifier.teddCardShadow(shape) else Modifier)
                        .teddClickable(
                            onClick = { onChange(value) },
                            shape = shape,
                            fill = if (on) TeddTokens.Surface else Color.Transparent,
                            hover = if (on) TeddTokens.Surface else Color.Transparent,
                            role = Role.Tab,
                        )
                        .height(TeddSize.ControlSm),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        lbl,
                        color = if (on) TeddTokens.Ink else TeddTokens.InkMuted,
                        style = TeddType.Caption.copy(fontWeight = FontWeight(if (on) 560 else 450)),
                        maxLines = 1,
                    )
                }
            }
        }
    }
}

/** Ligne qui mène ailleurs ou déclenche une action : icône, texte, chevron. */
@Composable
private fun SettingsActionRow(
    icon: String,
    title: String,
    description: String,
    destructive: Boolean,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .teddClickable(onClick = onClick)
            .padding(TeddSpace.S2),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3),
    ) {
        TeddIcon(name = icon, size = 18.dp, color = if (destructive) TeddTokens.Danger else TeddTokens.InkMuted)
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(title, color = if (destructive) TeddTokens.Danger else TeddTokens.Ink, style = TeddType.Label)
            Text(description, color = TeddTokens.InkMuted, style = TeddType.Caption)
        }
        TeddIcon(name = "chevron-right", size = 16.dp, color = TeddTokens.InkSubtle)
    }
}

@Composable
private fun AboutRow(label: String, value: String) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = TeddSpace.S2, vertical = TeddSpace.S1),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(label, color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.weight(1f))
        Text(value, color = TeddTokens.Ink, style = TeddType.MonoSm)
    }
}

/** État de la synchronisation : l'interrupteur, puis où on en est et le bouton pour la lancer. */
@Composable
private fun SyncStatusCard(
    state: org.teddapp.project.sync.SyncUiState,
    onToggle: (Boolean) -> Unit,
    onSyncNow: (() -> Unit)?,
) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
        SettingsToggle(
            icon = "repeat",
            title = "Synchronisation activée",
            description = "Envoie tes changements et récupère ceux des autres appareils.",
            on = state.enabled,
            onChange = onToggle,
        )
        if (!state.enabled) return@Column
        val syncing = state.status == org.teddapp.project.sync.SyncStatus.Syncing
        val statusLabel: String
        val statusColor: Color
        val statusIcon: String
        when (state.status) {
            org.teddapp.project.sync.SyncStatus.Idle -> {
                statusLabel = if (state.lastSyncAt == null) "Prêt, jamais synchronisé" else "À jour"
                statusColor = TeddTokens.Success
                statusIcon = "check"
            }
            org.teddapp.project.sync.SyncStatus.Syncing -> {
                statusLabel = "Synchronisation en cours…"
                statusColor = TeddTokens.InkMuted
                statusIcon = "clock"
            }
            org.teddapp.project.sync.SyncStatus.Error -> {
                statusLabel = "Le dernier cycle a échoué"
                statusColor = TeddTokens.Danger
                statusIcon = "alert"
            }
        }
        Box(Modifier.fillMaxWidth().padding(horizontal = TeddSpace.S2).height(1.dp).background(TeddTokens.Line))
        Column(modifier = Modifier.fillMaxWidth().padding(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S3)) {
                TeddIcon(name = statusIcon, size = 18.dp, color = statusColor, strokeWidth = 2.dp)
                Text(statusLabel, color = TeddTokens.Ink, style = TeddType.Label, modifier = Modifier.weight(1f))
                if (state.pendingChanges > 0) TeddTag("${state.pendingChanges} en attente")
            }
            if (state.errorMessage != null) {
                Text(state.errorMessage, color = TeddTokens.Danger, style = TeddType.Caption)
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = state.lastSyncAt?.let { "Dernière : ${formatRelativeTime(it)}" } ?: "Aucune synchronisation réussie pour l'instant",
                    color = TeddTokens.InkMuted,
                    style = TeddType.Caption,
                    modifier = Modifier.weight(1f),
                )
                if (onSyncNow != null) {
                    TeddButton(
                        text = "Synchroniser",
                        onClick = onSyncNow,
                        kind = TeddButtonKind.Primary,
                        size = TeddButtonSize.Sm,
                        enabled = !syncing,
                        icon = "repeat",
                    )
                }
            }
        }
    }
}

/** Format approximatif d'un timestamp en "il y a X". Simple, sans dépendance. */
private fun formatRelativeTime(epochMs: Long): String {
    val now = org.teddapp.project.db.nowMillis()
    val diffSec = ((now - epochMs) / 1000L).coerceAtLeast(0L)
    return when {
        diffSec < 5L -> "à l'instant"
        diffSec < 60L -> "il y a ${diffSec} s"
        diffSec < 3600L -> "il y a ${diffSec / 60L} min"
        diffSec < 86400L -> "il y a ${diffSec / 3600L} h"
        else -> "il y a ${diffSec / 86400L} j"
    }
}

@Composable
private fun ResetConfirmDialog(onCancel: () -> Unit, onConfirm: () -> Unit) {
    TeddConfirmDialog(
        title = "Réinitialiser toutes les données ?",
        message = "Toutes tes données locales seront supprimées : objectifs, projets, finances, notes, habitudes. Cette action est irréversible.",
        confirmLabel = "Tout réinitialiser",
        onConfirm = onConfirm,
        onDismiss = onCancel,
    )
}
